/**
 * FLX section 08 — the early-access lead form.
 *
 * Markup ported from the client's live site (`.signup-panel` / `.sp-*`). The live
 * version has no backend: it stashes submissions in `localStorage` and leaves a
 * `window.FLX_SUBMIT` hook with the comment *"expose a hook for the IT programmer
 * to POST to a real endpoint"*. This is that endpoint.
 *
 * It writes to `POST /storefront/feedback`, which is already public and
 * guest-friendly, notifies the tenant owners in their admin bell, and lands in
 * the existing admin **Feedback** inbox with `new → read → archived` triage. No
 * new table, route, or admin page — and deliberately NOT `contact_inquiries`,
 * whose only consumer in the repo is its own insert (nothing can read it).
 *
 * The three pathways ride the SUBJECT line so the inbox stays filterable without
 * adding a `lead` value to the shared category enum (which would put a stray
 * "Lead" option in every other tenant's feedback picker).
 */
import { useRef, useState, type FormEvent } from 'react';
import { submitFeedback } from '../../../lib/api';
import { getStoredRef } from '../../../lib/ref';
import { EARLY_ACCESS, PATHWAYS, PATHWAY_SUCCESS } from './tokens';

/** A second submit inside a minute is a bot or a double-click, never a person. */
const RESUBMIT_COOLDOWN_MS = 60_000;

type Fields = { fullName: string; email: string; phone: string; location: string };
const EMPTY: Fields = { fullName: '', email: '', phone: '', location: '' };

export function EarlyAccessForm() {
  const [interest, setInterest] = useState(PATHWAYS[0]!.value);
  const [fields, setFields] = useState<Fields>(EMPTY);
  const [validated, setValidated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const lastSubmit = useRef(0);
  // Honeypot: a real person never fills a field they can't see.
  const trap = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);

  const set = (k: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setFields((f) => ({ ...f, [k]: e.target.value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setValidated(true);
    if (busy) return;
    if (trap.current?.value) return;
    const form = formRef.current;
    if (form && !form.checkValidity()) {
      form.querySelector<HTMLInputElement>(':invalid')?.focus();
      return;
    }
    const now = Date.now();
    if (now - lastSubmit.current < RESUBMIT_COOLDOWN_MS) {
      setError('You just sent that — give it a minute.');
      return;
    }
    setBusy(true);
    setError(null);
    const ref = getStoredRef();
    try {
      await submitFeedback({
        category: 'other',
        name: fields.fullName,
        email: fields.email,
        subject: `FLX Early Access — ${interest}`,
        message: [
          `Pathway: ${interest}`,
          `Contact: ${fields.phone}`,
          `Location: ${fields.location}`,
          ref ? `Referred by: ${ref}` : null,
        ]
          .filter(Boolean)
          .join('\n'),
        pageUrl: window.location.href,
      });
      lastSubmit.current = now;
      setDone(PATHWAY_SUCCESS[interest] ?? PATHWAY_SUCCESS['Early Access']!);
      setFields(EMPTY);
      setValidated(false);
    } catch {
      setError("That didn't send. Please try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="signup-panel">
        <div className="sp-corner" />
        <div className="sp-success">
          <div className="sp-success-bolt">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M13 2 L4 14 L11 14 L9 22 L20 9 L13 9 Z" fill="var(--orange)" />
            </svg>
          </div>
          <h3>{EARLY_ACCESS.success.title}</h3>
          <p>{done}</p>
          <button type="button" className="btn btn--ghost sp-reset" onClick={() => setDone(null)}>
            {EARLY_ACCESS.success.again} <span className="arrow">→</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="signup-panel">
      <div className="sp-corner" />
      <form
        ref={formRef}
        className={`flx-form${validated ? ' was-validated' : ''}`}
        noValidate
        onSubmit={onSubmit}
      >
        <fieldset className="sp-tabs" aria-label="What are you interested in?">
          <legend className="sp-field-label">
            {EARLY_ACCESS.formLabel}
            <span className="req">*</span>
          </legend>
          <div className="sp-tab-row">
            {PATHWAYS.map((p) => (
              <label key={p.value} className={`sp-tab${interest === p.value ? ' is-active' : ''}`}>
                <input
                  type="radio"
                  name="interest"
                  value={p.value}
                  checked={interest === p.value}
                  onChange={() => setInterest(p.value)}
                />
                <span className="sp-tab-no">{p.n}</span>
                <span className="sp-tab-name">{p.value}</span>
                <span className="sp-tab-desc">{p.sub}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <Field
          id="f-name"
          label="Full Name"
          placeholder="Juan Dela Cruz"
          autoComplete="name"
          error="Please enter your name."
          value={fields.fullName}
          onChange={set('fullName')}
        />

        <div className="sp-field-grid">
          <Field
            id="f-email"
            type="email"
            label="Email"
            placeholder="you@email.com"
            autoComplete="email"
            error="Enter a valid email."
            value={fields.email}
            onChange={set('email')}
          />
          <Field
            id="f-phone"
            type="tel"
            label="Contact Number"
            placeholder="+63 9XX XXX XXXX"
            autoComplete="tel"
            error="Enter your number."
            value={fields.phone}
            onChange={set('phone')}
          />
        </div>

        <Field
          id="f-location"
          label="Location"
          placeholder="City / Province"
          autoComplete="address-level2"
          error="Where are you based?"
          value={fields.location}
          onChange={set('location')}
        />

        {/* Honeypot — off-screen, never announced, never tabbable. */}
        <input
          ref={trap}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }}
        />

        <button type="submit" className="btn btn--primary sp-submit" disabled={busy}>
          <span className="sp-submit-label">{busy ? 'Sending…' : EARLY_ACCESS.submit}</span>
          <span className="arrow">→</span>
        </button>

        {error && (
          <p className="sp-form-error" role="alert">
            {error}
          </p>
        )}
        <p className="sp-fineprint">{EARLY_ACCESS.microcopy}</p>
      </form>
    </div>
  );
}

function Field({
  id,
  label,
  error,
  value,
  onChange,
  type = 'text',
  placeholder,
  autoComplete,
}: {
  id: string;
  label: string;
  error: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <div className="sp-field">
      <label className="sp-field-label" htmlFor={id}>
        {label}
        <span className="req">*</span>
      </label>
      <input
        className="sp-input"
        id={id}
        name={id}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required
        value={value}
        onChange={onChange}
      />
      <span className="sp-error" data-for={id}>
        {error}
      </span>
    </div>
  );
}
